import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../lib/api'
import { toast } from '../lib/toast'
import { useTemplates } from '../lib/queries'
import type { Overlay } from '../types'
import { getForm } from '../templates'
import AppShell from '../components/AppShell'
import Badge from '../components/ui/Badge'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import OverlayPreview from '../components/OverlayPreview'

function CopyBtn({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          /* ignore */
        }
      }}
      title="Kopyala"
      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[var(--color-success)]">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
      {label && <span>{label}</span>}
    </button>
  )
}

export default function OverlayEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: templates = [] } = useTemplates()
  const [overlay, setOverlay] = useState<Overlay | null>(null)
  const [error, setError] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')

  useEffect(() => {
    if (!id) return
    api<Overlay>(`/api/overlays/${id}`)
      .then((o) => {
        setOverlay(o)
        setTitleDraft(o.title || '')
      })
      .catch((e: ApiError) => {
        if (e.status === 401) navigate('/login')
        else setError(e.message || 'Yüklenemedi')
      })
  }, [id, navigate])

  if (error) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-6 py-8 text-sm text-[var(--color-danger)]">{error}</div>
      </AppShell>
    )
  }
  if (!overlay) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-6 py-8 text-sm text-[var(--color-text-muted)]">
          Yükleniyor…
        </div>
      </AppShell>
    )
  }

  const Form = getForm(overlay.type, overlay.variant)
  const url = `https://kick.pxlab.dev/o/${overlay.token}`
  const tmpl = templates.find((t) => t.type === overlay.type && t.variant === overlay.variant)
  const recW = tmpl?.recommended_width ?? 1920
  const recH = tmpl?.recommended_height ?? 1080
  const displayName = overlay.title || overlay.name

  async function patchOverlay(patch: Partial<{ title: string; is_active: boolean }>) {
    if (!overlay) return
    try {
      const updated = await api<Overlay>(`/api/overlays/${overlay.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      })
      setOverlay(updated)
      qc.invalidateQueries({ queryKey: ['channels', overlay.channel_id, 'overlays'] })
      return updated
    } catch (e) {
      toast.error((e as Error).message)
    }
  }

  return (
    <AppShell activeChannelId={overlay.channel_id}>
      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate(`/channels/${overlay.channel_id}`)}
            className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-2"
          >
            ← Kanala dön
          </button>
          <div className="flex items-center gap-3 flex-wrap">
            {editingTitle ? (
              <form
                onSubmit={async (e) => {
                  e.preventDefault()
                  await patchOverlay({ title: titleDraft.trim() })
                  toast.success('Başlık güncellendi')
                  setEditingTitle(false)
                }}
                className="flex items-center gap-2"
              >
                <Input
                  value={titleDraft}
                  onChange={(e) => setTitleDraft(e.target.value)}
                  placeholder={overlay.name}
                  autoFocus
                  className="text-base h-9"
                />
                <Button type="submit" variant="primary" size="sm">
                  Kaydet
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setTitleDraft(overlay.title || '')
                    setEditingTitle(false)
                  }}
                >
                  İptal
                </Button>
              </form>
            ) : (
              <>
                <button
                  onClick={() => setEditingTitle(true)}
                  className="text-xl font-semibold hover:text-[var(--color-accent-hover)] cursor-text text-left"
                  title="Başlığı düzenle"
                >
                  {displayName}
                </button>
                <Badge tone="accent">
                  {overlay.type}/{overlay.variant}
                </Badge>
                <label className="ml-auto flex items-center gap-1.5 text-xs text-[var(--color-text-muted)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={overlay.is_active}
                    onChange={(e) => patchOverlay({ is_active: e.target.checked })}
                    className="accent-[var(--color-accent)]"
                  />
                  <span>{overlay.is_active ? 'Aktif' : 'Pasif'}</span>
                </label>
              </>
            )}
          </div>
        </div>

        {/* Sticky preview üstte — full width 16:9 max-h-[420px] */}
        <div className="sticky top-3 z-10 -mx-2 px-2">
          <OverlayPreview token={overlay.token} maxHeightPx={420} />
        </div>

        {/* OBS bilgi paneli */}
        <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <h3 className="text-sm font-semibold mb-3">OBS browser source</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                URL
              </div>
              <div className="flex items-center gap-1">
                <code className="text-xs font-mono truncate flex-1 px-2 py-1.5 rounded bg-[var(--color-elevated)] border border-[var(--color-border)]">
                  {url}
                </code>
                <CopyBtn text={url} />
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Yeni sekmede aç"
                  className="p-1.5 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                Önerilen genişlik
              </div>
              <div className="flex items-center gap-1">
                <code className="text-xs font-mono px-2 py-1.5 rounded bg-[var(--color-elevated)] border border-[var(--color-border)]">
                  {recW}px
                </code>
                <CopyBtn text={String(recW)} />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                Önerilen yükseklik
              </div>
              <div className="flex items-center gap-1">
                <code className="text-xs font-mono px-2 py-1.5 rounded bg-[var(--color-elevated)] border border-[var(--color-border)]">
                  {recH}px
                </code>
                <CopyBtn text={String(recH)} />
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[var(--color-text-subtle)] mt-3">
            OBS &rarr; Sources &rarr; Add &rarr; Browser &rarr; URL'i yapıştır, genişlik &times; yükseklik
            değerlerini gir. Pasif overlay'ler render edilmez (OBS source'u kalır).
          </p>
        </section>

        {/* Form */}
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
          {Form ? (
            <Form overlayId={overlay.id} initial={overlay.state} />
          ) : (
            <div className="text-sm text-[var(--color-warning)]">
              Bu template ({overlay.type}/{overlay.variant}) için form henüz mevcut değil.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  )
}
